import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { AdaptiveTabs, AdaptiveTabItem, useAdaptiveTab } from '@components/common/AdaptiveTabs.tsx';
import { toastService } from '@services/toast.service.ts';
import { AdminClientPaymentsDevTools } from '@components/admin/AdminClientPaymentsDevTools.tsx';
import {
  nativeIapAdminService,
  NativeIapAttempt,
  NativeIapHealth,
  NativeIapProvider,
  NativeIapSubscription,
  NativeIapTransaction,
  NativeIapWebhookLog,
} from '@services/native-iap-admin.service.ts';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString('uk-UA', { timeZone: userTimeZone });

const userLabel = (u: { name: string; surname: string | null; email: string } | null | undefined): string => {
  if (!u) return '—';
  return [u.name, u.surname].filter(Boolean).join(' ') || u.email;
};

type DevNativeIapTab = 'transactions' | 'subscriptions' | 'attempts' | 'webhooks' | 'dev-tools';

const TABS: (AdaptiveTabItem & { value: DevNativeIapTab })[] = [
  { value: 'transactions', label: 'Транзакції', iDoc: TabIndices.DEV_NATIVE_IAP_TRANSACTIONS },
  { value: 'subscriptions', label: 'Підписки', iDoc: TabIndices.DEV_NATIVE_IAP_SUBSCRIPTIONS },
  { value: 'attempts', label: 'Спроби оплати', iDoc: TabIndices.DEV_NATIVE_IAP_ATTEMPTS },
  { value: 'webhooks', label: 'Логи вебхуків', iDoc: TabIndices.DEV_NATIVE_IAP_WEBHOOKS },
  { value: 'dev-tools', label: 'Dev Tools', iDoc: TabIndices.DEV_NATIVE_IAP_DEV_TOOLS },
];

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  Approved: 'success',
  processed: 'success',
  pending: 'warning',
  payment_failed: 'error',
  failed: 'error',
  cancelled: 'default',
  expired: 'default',
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <Chip size="small" label={status} color={STATUS_COLOR[status] ?? 'default'} variant="outlined" />
);

const NativeTransactionsTab: React.FC<{ provider: NativeIapProvider }> = ({ provider }) => {
  const [rows, setRows] = useState<NativeIapTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nativeIapAdminService.getTransactions(provider, { limit, offset: (page - 1) * limit });
      setRows(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити транзакції.');
    } finally {
      setLoading(false);
    }
  }, [provider, page, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [provider]);

  if (loading) return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
  if (rows.length === 0) return <Typography sx={{ mt: 4, textAlign: 'center' }}>Транзакцій ще немає.</Typography>;

  return (
    <>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Користувач</TableCell>
              <TableCell>Order reference</TableCell>
              <TableCell>Сума</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Дата</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((tx) => (
              <TableRow key={tx.id} hover>
                <TableCell>
                  <Typography variant="body2">{userLabel(tx.user)}</Typography>
                  <Typography variant="caption" color="text.secondary">{tx.user?.email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{tx.orderReference}</Typography>
                </TableCell>
                <TableCell>{tx.amount} {tx.currency}</TableCell>
                <TableCell><StatusChip status={tx.status} /></TableCell>
                <TableCell>{tx.type}</TableCell>
                <TableCell>{formatDate(tx.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey={`pagination:dev-native-iap-transactions-${provider}`}
      />
    </>
  );
};

const NativeSubscriptionsTab: React.FC<{ provider: NativeIapProvider }> = ({ provider }) => {
  const [rows, setRows] = useState<NativeIapSubscription[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nativeIapAdminService.getSubscriptions(provider, { limit, offset: (page - 1) * limit });
      setRows(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити підписки.');
    } finally {
      setLoading(false);
    }
  }, [provider, page, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [provider]);

  if (loading) return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
  if (rows.length === 0) return <Typography sx={{ mt: 4, textAlign: 'center' }}>Підписок ще немає.</Typography>;

  const externalIdLabel = provider === 'apple' ? 'Original Transaction ID' : 'Purchase Token';

  return (
    <>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Користувач</TableCell>
              <TableCell>План</TableCell>
              <TableCell>Цикл</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Тріал</TableCell>
              <TableCell>Environment</TableCell>
              <TableCell>{externalIdLabel}</TableCell>
              <TableCell>Наступний платіж</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((sub) => {
              const environment = provider === 'apple' ? sub.appleEnvironment : sub.googleEnvironment;
              const externalId = provider === 'apple' ? sub.appleOriginalTransactionId : sub.googlePurchaseToken;

              return (
                <TableRow key={sub.id} hover>
                  <TableCell>
                    <Typography variant="body2">{userLabel(sub.user)}</Typography>
                    <Typography variant="caption" color="text.secondary">{sub.user?.email}</Typography>
                  </TableCell>
                  <TableCell>{sub.plan?.name ?? '—'}</TableCell>
                  <TableCell>{sub.billingCycle}</TableCell>
                  <TableCell><StatusChip status={sub.status} /></TableCell>
                  <TableCell>{sub.isTrial ? 'так' : 'ні'}</TableCell>
                  <TableCell>
                    {environment && (
                      <Chip size="small" label={environment} color={environment === 'production' ? 'primary' : 'default'} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={externalId ?? ''}>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace', maxWidth: 160, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {externalId ?? '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{sub.nextPaymentDate ? formatDate(sub.nextPaymentDate) : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey={`pagination:dev-native-iap-subscriptions-${provider}`}
      />
    </>
  );
};

const NativeWebhookLogsTab: React.FC<{ provider: NativeIapProvider }> = ({ provider }) => {
  const [rows, setRows] = useState<NativeIapWebhookLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nativeIapAdminService.getWebhookLogs(provider, { limit, offset: (page - 1) * limit });
      setRows(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити лог вебхуків.');
    } finally {
      setLoading(false);
    }
  }, [provider, page, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [provider]);

  if (loading) return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
  if (rows.length === 0) return <Typography sx={{ mt: 4, textAlign: 'center' }}>Вебхуків ще не надходило.</Typography>;

  return (
    <>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order reference</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Помилка</TableCell>
              <TableCell>Payload</TableCell>
              <TableCell>Дата</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{log.orderReference ?? '—'}</Typography>
                </TableCell>
                <TableCell><StatusChip status={log.status} /></TableCell>
                <TableCell>
                  {log.errorMessage && (
                    <Typography variant="caption" color="error">{log.errorMessage}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={<pre style={{ margin: 0, maxWidth: 500, whiteSpace: 'pre-wrap' }}>{JSON.stringify(log.payload, null, 2)}</pre>}>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', maxWidth: 300, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}
                    >
                      {JSON.stringify(log.payload)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatDate(log.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey={`pagination:dev-native-iap-webhooks-${provider}`}
      />
    </>
  );
};

const REASON_LABEL: Record<string, string> = {
  signature_invalid: 'Невалідний підпис',
  incomplete_payload: 'Неповний payload',
  revoked: 'Транзакцію відкликано',
  expired: 'Термін дії сплив',
  plan_not_mapped: 'План не змаплено',
  provider_api_failure: 'Помилка API провайдера',
};

const NativeAttemptsTab: React.FC<{ provider: NativeIapProvider }> = ({ provider }) => {
  const [rows, setRows] = useState<NativeIapAttempt[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nativeIapAdminService.getAttempts(provider, { limit, offset: (page - 1) * limit });
      setRows(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити спроби оплати.');
    } finally {
      setLoading(false);
    }
  }, [provider, page, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [provider]);

  if (loading) return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
  if (rows.length === 0) return <Typography sx={{ mt: 4, textAlign: 'center' }}>Спроб оплати ще не було.</Typography>;

  return (
    <>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Результат</TableCell>
              <TableCell>Користувач</TableCell>
              <TableCell>Причина</TableCell>
              <TableCell>Помилка</TableCell>
              <TableCell>Продукт</TableCell>
              <TableCell>Ідентифікатор</TableCell>
              <TableCell>Дата</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell>
                  <Chip
                    size="small"
                    label={a.success ? 'успіх' : 'провал'}
                    color={a.success ? 'success' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{a.userId ?? '—'}</TableCell>
                <TableCell>{a.reason ? (REASON_LABEL[a.reason] ?? a.reason) : '—'}</TableCell>
                <TableCell>
                  {a.errorMessage && (
                    <Tooltip title={a.errorMessage}>
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ maxWidth: 240, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}
                      >
                        {a.errorMessage}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{a.productId ?? '—'}</TableCell>
                <TableCell>
                  <Tooltip title={a.rawIdentifier ?? ''}>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', maxWidth: 160, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {a.rawIdentifier ?? '—'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatDate(a.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UniversalPagination
        page={page}
        count={Math.ceil(total / limit)}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        storageKey={`pagination:dev-native-iap-attempts-${provider}`}
      />
    </>
  );
};

const NativeHealthBanner: React.FC<{ provider: NativeIapProvider }> = ({ provider }) => {
  const [health, setHealth] = useState<NativeIapHealth | null>(null);

  useEffect(() => {
    let cancelled = false;
    nativeIapAdminService.getHealth(provider)
      .then((res) => { if (!cancelled) setHealth(res); })
      .catch(() => { if (!cancelled) setHealth(null); });
    return () => { cancelled = true; };
  }, [provider]);

  if (!health) return null;

  const checks: { label: string; ok: boolean }[] = provider === 'apple'
    ? [
        { label: `bundleId: ${health.bundleId ?? '—'}`, ok: Boolean(health.bundleId) },
        { label: 'Ключі підпису', ok: Boolean(health.hasSigningCredentials) },
        { label: `Root-сертифікати (${health.rootCertificatesCount ?? 0})`, ok: Boolean(health.rootCertificatesLoaded) },
        { label: health.productionVerificationEnabled ? 'Production-верифікація' : 'Sandbox-верифікація', ok: true },
      ]
    : [
        { label: `packageName: ${health.packageName ?? '—'}`, ok: Boolean(health.packageName) },
        { label: 'Android Publisher API', ok: Boolean(health.androidPublisherConfigured) },
        { label: 'RTDN (webhook)', ok: Boolean(health.rtdnConfigured) },
      ];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      {checks.map((c) => (
        <Chip key={c.label} size="small" label={c.label} color={c.ok ? 'success' : 'warning'} variant="outlined" />
      ))}
    </Box>
  );
};

const DevNativeIapPageContent: React.FC = () => {
  const [provider, setProvider] = useState<NativeIapProvider>('apple');
  const currentTab = useAdaptiveTab(TABS, { searchParamKey: 'tab', defaultValue: 'transactions', storageKey: 'tab:dev-native-iap' }) as DevNativeIapTab;
  const currentTabMeta = TABS.find((t) => t.value === currentTab)!;
  const isDevTools = currentTab === 'dev-tools';

  return (
    <Box data-i-doc={PageIndices.DEV_NATIVE_IAP}>
      <PageHint iDoc={PageIndices.DEV_NATIVE_IAP} />

      <PageCard>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AdaptiveTabs tabs={TABS} searchParamKey="tab" defaultValue="transactions" storageKey="tab:dev-native-iap" />
          </Box>

          {!isDevTools && (
            <ToggleButtonGroup
              value={provider}
              exclusive
              size="small"
              onChange={(_, v) => v && setProvider(v)}
              aria-label="native iap provider"
            >
              <ToggleButton value="apple">Apple</ToggleButton>
              <ToggleButton value="google">Google</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {!isDevTools && <NativeHealthBanner provider={provider} />}

        <Box sx={{ pt: 2 }} data-i-doc={currentTabMeta.iDoc}>
          {currentTab === 'transactions' && <NativeTransactionsTab provider={provider} />}
          {currentTab === 'subscriptions' && <NativeSubscriptionsTab provider={provider} />}
          {currentTab === 'attempts' && <NativeAttemptsTab provider={provider} />}
          {currentTab === 'webhooks' && <NativeWebhookLogsTab provider={provider} />}
          {currentTab === 'dev-tools' && <AdminClientPaymentsDevTools />}
        </Box>
      </PageCard>
    </Box>
  );
};

export const DevNativeIapPage: React.FC = () => (
  <DashboardLayout>
    <DevNativeIapPageContent />
  </DashboardLayout>
);
